import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, throwError, BehaviorSubject, lastValueFrom } from "rxjs";
import { JwtHelperService } from "@auth0/angular-jwt";
import { environment } from "src/environments/environment";
import { BrowserStorage } from "../core/storage";
@Injectable({
  providedIn: "root",
})
export class AuthService {
  public currentUser: Observable<any>;
  private currentUserSubject: BehaviorSubject<any>;
  newApiUrl = environment.APIURL;
  user = { username: "", authorization: "" };
  memoryStorage = { currentUser: "" };
  constructor(private http: HttpClient) {
    const token = this.getStoredToken();
    this.currentUserSubject = new BehaviorSubject<any>(token);

    this.currentUser = this.currentUserSubject.asObservable();
  }

  async JWTlogin(username: string, password: string): Promise<string> {
    const r = await lastValueFrom(this.http.post<any>(this.newApiUrl + "/login", { username, password }));
    const token = r.token;
    const currentToken = this.getToken();
    const previousDecoded = currentToken ? this.JWTDecode(currentToken) : {};
    const nextDecoded = this.JWTDecode(token) || {};
    const previousSubject = previousDecoded?.sub;
    const nextSubject = nextDecoded?.sub;
    const isTrialLogin = !!nextDecoded?.trial;

    if ((previousSubject && previousSubject !== nextSubject) || isTrialLogin) {
      this.clearRememberedContractState();
    }

    BrowserStorage.setString("currentUser", token);
    this.currentUserSubject.next(token);
    return token;
  }

  getUser() {
    return this.user.username;
  }

  getAuthorization() {
    return this.user.authorization;
  }

  getToken() {
    const stored = this.getStoredToken();
    if (stored != null) return stored;
    return this.memoryStorage.currentUser;
  }

  logout() {
    if (this.isStorageAvailable()) {
      const preservedLocalEntries = this.getPreservedStorageEntries(localStorage);
      const preservedSessionEntries = this.getPreservedStorageEntries(sessionStorage);
      BrowserStorage.delete("currentUser");
      BrowserStorage.delete("currentUser", false);
      BrowserStorage.delete("trial", false);
      BrowserStorage.delete("trial");
      BrowserStorage.delete("activeLics", false);
      BrowserStorage.delete("activeLics");
      BrowserStorage.delete("logout");
      this.clearRememberedContractState();
      localStorage.clear();
      sessionStorage.clear();
      preservedLocalEntries.forEach(([key, value]) => localStorage.setItem(key, value));
      preservedSessionEntries.forEach(([key, value]) => sessionStorage.setItem(key, value));
    } else {
      this.memoryStorage.currentUser = "";
    }
    this.currentUserSubject.next(null);
    return true;
  }

  isLoggedIn(): boolean {
    const token = this.getToken();
    if (token === "" || token == null) {
      return false;
    }

    const jwtHelper = new JwtHelperService();
    try {
      return !jwtHelper.isTokenExpired(token);
    } catch {
      return false;
    }
  }

  JWTDecode(token) {
    const jwtHelper = new JwtHelperService();
    if (token == null || token == undefined) {
      return {};
    }
    try {
      return jwtHelper.decodeToken(token);
    } catch {
      return {};
    }
  }

  isStorageAvailable() {
    const key = "&Tv7Ns3g*~8bd3N/tdkx(pNmbwkHj.!r";
    try {
      localStorage.setItem("storagetest", key);
      return true;
    } catch {
      return false;
    }
  }

  errorHandl(error) {
    return throwError(error);
  }

  public get currentToken(): any {
    return this.currentUserSubject.value;
  }

  private getStoredToken(): string | null {
    if (!this.isStorageAvailable()) return null;
    return BrowserStorage.getString("currentUser") || BrowserStorage.getString("currentUser", false);
  }

  private getPreservedStorageEntries(storage: Storage): [string, string][] {
    const preservedPrefixes = ["activation-filter-", "activation-show-"];
    const preservedKeys = new Set(["acceptcookies", "theme", "shell.sidenav.pinned"]);
    const entries: [string, string][] = [];

    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (!key) continue;
      if (!preservedKeys.has(key) && !preservedPrefixes.some((prefix) => key.startsWith(prefix))) continue;

      const value = storage.getItem(key);
      if (value != null) entries.push([key, value]);
    }

    return entries;
  }

  private clearRememberedContractState(): void {
    BrowserStorage.delete("shell.selectedContractId");
    BrowserStorage.delete("shell.selectedContractId", false);
    BrowserStorage.delete("shell.selectedContractOwner");
    BrowserStorage.delete("shell.selectedContractOwner", false);
  }
}

export const AUTH_PROVIDERS: any[] = [{ provide: AuthService, useClass: AuthService }];
